import React from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { getCurrentYear } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/commonUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { resolveSessionLimit } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { ProgramCoverageBreakdown } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'

import { healthPlanStates, programs } from '../../../../../common/constants/appConstants'

const InlineText = styled(BodyText)`
  display: contents;
`

const successFormatter = (colors: { textSuccess: string }) => (chunks: string) => (
  <InlineText bold color={colors.textSuccess} size={BodyTextSize.DEFAULT} text={chunks} />
)

const warningFormatter = (colors: { textWarning: string }) => (chunks: string) => (
  <InlineText bold color={colors.textWarning} size={BodyTextSize.DEFAULT} text={chunks} />
)

const errorFormatter = (colors: { textError: string }) => (chunks: string) => (
  <InlineText bold color={colors.textError} size={BodyTextSize.DEFAULT} text={chunks} />
)

/**
 * Custom hook that returns a formatted message for health plan status
 * @param program - The program type
 * @param programCoverageBreakdown - The program coverage breakdown
 * @param status - The health plan status
 * @param clientDetails - client details
 * @returns A formatted message string
 */
export const useHealthPlanStatus = ({
  programName,
  programCoverageBreakdown,
  status,
  clientDetails,
  isZeroDollarCostShare = false,
}: {
  programName?: string
  programCoverageBreakdown: ProgramCoverageBreakdown | undefined
  status: string | undefined
  clientDetails?: ClientObject
  isZeroDollarCostShare?: boolean
}): React.ReactNode => {
  const { colors } = useTheme()
  const { formatMessage } = useIntl()
  const { forceAdultFlowsToHPI } = useFlags()

  const { READY, NOT_READY, NOT_ELIGIBLE, HEALTH_PLAN_PAYMENT_MISSING } = healthPlanStates
  const currentProgramCoverageBreakdown = programCoverageBreakdown
    ? programCoverageBreakdown[getCurrentYear()] || {}
    : {}
  const {
    alcoholUseDisorderTherapy,
    coachingForParents,
    considerMeds,
    stressManagement,
    considerLyraTherapy,
    dialecticalBehaviorTherapy,
    dialecticalBehaviorTherapyTeens,
  } = currentProgramCoverageBreakdown
  const hasEAPSessions = resolveSessionLimit(programName, clientDetails, forceAdultFlowsToHPI, true) !== 0

  const success = successFormatter(colors)
  const warning = warningFormatter(colors)
  const error = errorFormatter(colors)

  const isEapOnly = (
    coverage: { bhbSupported?: boolean; eapSupported?: boolean; specialtyBhbSupported?: boolean } | undefined,
  ) => {
    return !coverage?.bhbSupported && coverage?.eapSupported && !coverage?.specialtyBhbSupported
  }

  // ICE-6076 / HPS-4812 - For customers with zero dollar cost share,
  // payment information is not required, so we remove payment-related messaging.
  if (isZeroDollarCostShare) {
    switch (status) {
      case NOT_READY:
        return hasEAPSessions
          ? formatMessage(
              {
                defaultMessage:
                  'If you and your client intend to continue care past the covered EAP sessions, your client <warning>needs to add their health insurance information</warning> to their profile to continue care.',
                description:
                  'Message when client with EAP sessions needs to add their health insurance information but not payment information',
              },
              { warning },
            )
          : formatMessage(
              {
                defaultMessage:
                  'Your client <warning>needs to add their health insurance information</warning> to their profile to continue care.',
                description:
                  'Message when client needs to add their health insurance information but not payment information',
              },
              { warning },
            )
      case HEALTH_PLAN_PAYMENT_MISSING:
        return hasEAPSessions
          ? formatMessage(
              {
                defaultMessage:
                  'If you and your client intend to continue care past the covered EAP sessions, your client is <success>ready to use their eligible health insurance to pay for sessions.</success>',
                description:
                  'Message when client with EAP sessions is ready to use health plan with missing payment information',
              },
              { success },
            )
          : formatMessage(
              {
                defaultMessage:
                  'Your client is <success>ready to use their eligible health insurance to pay for sessions.</success>',
                description: 'Message when client is ready to use health plan with missing payment information',
              },
              { success },
            )
    }
  }

  // Special case for customers with self-pay enabled and EAP only coverage.
  // Only applies to LCC, LCT, AUD and Coaching For Parents
  if (clientDetails?.customer_support_self_pay) {
    switch (programName) {
      case programs.Coaching:
        if (isEapOnly(stressManagement)) {
          return undefined
        }
        break
      case programs.BlendedCareTherapy:
        if (isEapOnly(considerLyraTherapy)) {
          return undefined
        }
        break
      case programs.AlcoholUseDisorderTherapy:
        if (isEapOnly(alcoholUseDisorderTherapy)) {
          return undefined
        }
        break
      case programs.CoachingForParents:
        if (isEapOnly(coachingForParents)) {
          return undefined
        }
        break
    }
  }

  switch (programName) {
    case programs.MedicationManagement:
      if (!considerMeds?.eapSupported) {
        switch (status) {
          case READY:
            return formatMessage(
              {
                defaultMessage: 'Your client is <success>ready to use their health plan for their sessions.</success>',
                description: 'Message when medication management client is ready to use health plan',
              },
              { success },
            )
          case NOT_ELIGIBLE:
            return formatMessage(
              {
                defaultMessage:
                  'Your clientʼs <error>health plan does not cover Lyra.</error> Ask your client to contact the Care Navigator Team at care@lyrahealth.com.',
                description: 'Message when medication management client is not eligible for health plan',
              },
              { error },
            )
          case HEALTH_PLAN_PAYMENT_MISSING:
            return formatMessage(
              {
                defaultMessage:
                  'Your client has entered their health plan information but is missing their payment card for copayments. Please have your client <warning>update their payment card for their health plan section</warning> to complete their profile.',
                description: 'Message when client is missing payment information',
              },
              { warning },
            )
          case NOT_READY:
            return formatMessage(
              {
                defaultMessage:
                  'Your client <warning>needs to enter both their health plan and payment information</warning> in their profile to determine coverage for their sessions.',
                description: 'Message when medication management client needs to enter health plan information',
              },
              { warning },
            )
        }
      }
      break
    // falls through to the default cases if meds is supported through EAP
    case programs.Coaching:
    case programs.SingleSessionCoaching:
      if (
        stressManagement?.bhbSupported &&
        stressManagement?.eapSupported &&
        !stressManagement?.specialtyBhbSupported
      ) {
        switch (status) {
          case READY:
            return formatMessage(
              {
                defaultMessage:
                  'If you and your client intend to continue care past the covered EAP sessions, your client is <success>ready to use their eligible health plan insurance to pay for sessions.</success>',
                description: 'Message when coaching client with EAP is ready to use health plan',
              },
              { success },
            )
          case NOT_ELIGIBLE:
            return formatMessage(
              {
                defaultMessage:
                  'Your clientʼs <error>health insurance does not cover Lyra and care should be discontinued</error> past the covered EAP sessions.',
                description: 'Message when coaching client with EAP is not eligible for health plan',
              },
              { error },
            )
          case HEALTH_PLAN_PAYMENT_MISSING:
            return formatMessage(
              {
                defaultMessage:
                  'Your client has entered their health plan information but is missing their payment card for copayments. Please have your client <warning>update their payment card</warning> for their health plan section to complete their profile.',
                description: 'Message when client is missing payment information',
              },
              { warning },
            )
          case NOT_READY:
            return formatMessage(
              {
                defaultMessage:
                  'If you and your client intend to continue care past the covered EAP sessions, your client needs to <warning>add their health insurance and payment information</warning> to their profile.',
                description: 'Message when coaching client with EAP needs to add health plan information',
              },
              { warning },
            )
        }
      } else if (
        stressManagement?.bhbSupported &&
        !stressManagement?.eapSupported &&
        !stressManagement?.specialtyBhbSupported
      ) {
        switch (status) {
          case READY:
            return formatMessage(
              {
                defaultMessage:
                  'Your client is <success>ready to use their eligible health insurance to pay for sessions.</success>',
                description: 'Message when coaching client is ready to use health plan',
              },
              { success },
            )
          case NOT_ELIGIBLE:
            return formatMessage(
              {
                defaultMessage:
                  'Your clientʼs <error>health insurance does not cover Lyra and care should be discontinued.</error>',
                description: 'Message when coaching client is not eligible for health plan',
              },
              { error },
            )
          case HEALTH_PLAN_PAYMENT_MISSING:
            return formatMessage(
              {
                defaultMessage:
                  'Your client has entered their health plan information but is missing their payment card for copayments. Please have your client <warning>update their payment card for their health plan section</warning> to complete their profile.',
                description: 'Message when client is missing payment information',
              },
              { warning },
            )
          case NOT_READY:
            return formatMessage(
              {
                defaultMessage:
                  'Your client <warning>needs to add their health insurance and payment information</warning> to their profile to continue care.',
                description: 'Message when coaching client needs to add health plan information',
              },
              { warning },
            )
        }
      } else {
        return formatMessage(
          {
            defaultMessage:
              'Your client <error>does not have coverage through an eligible health plan</error> for this program.',
            description: 'Message when client does not have coverage for the program',
          },
          { error },
        )
      }
      break
    case programs.BlendedCareTherapy:
    case programs.DialecticalBehaviorTherapy:
    case programs.DialecticalBehaviorTherapyTeens: {
      const therapyCoverage =
        programName === programs.DialecticalBehaviorTherapyTeens
          ? dialecticalBehaviorTherapyTeens
          : programName === programs.DialecticalBehaviorTherapy
            ? dialecticalBehaviorTherapy
            : considerLyraTherapy
      if (therapyCoverage?.bhbSupported && therapyCoverage?.eapSupported && !therapyCoverage?.specialtyBhbSupported) {
        switch (status) {
          case READY:
            return formatMessage(
              {
                defaultMessage:
                  'If you and your client intend to continue care past the covered EAP sessions, your client is <success>ready to use their eligible health plan insurance to pay for sessions.</success>',
                description: 'Message when therapy client with EAP is ready to use health plan',
              },
              { success },
            )
          case NOT_ELIGIBLE:
            return formatMessage(
              {
                defaultMessage:
                  'Your clientʼs <error>health insurance does not cover Lyra and care should be discontinued</error> past the covered EAP sessions.',
                description: 'Message when therapy client with EAP is not eligible for health plan',
              },
              { error },
            )
          case HEALTH_PLAN_PAYMENT_MISSING:
            return formatMessage(
              {
                defaultMessage:
                  'Your client has entered their health plan information but is missing their payment card for copayments. Please have your client <warning>update their payment card for their health plan section</warning> to complete their profile.',
                description: 'Message when client is missing payment information',
              },
              { warning },
            )
          case NOT_READY:
            return formatMessage(
              {
                defaultMessage:
                  'If you and your client intend to continue care past the covered EAP sessions, your client needs to <warning>add their health insurance and payment information</warning> to their profile.',
                description: 'Message when therapy client with EAP needs to add health plan information',
              },
              { warning },
            )
        }
      } else if (
        !therapyCoverage?.bhbSupported &&
        therapyCoverage?.eapSupported &&
        therapyCoverage?.specialtyBhbSupported
      ) {
        switch (status) {
          case READY:
            return formatMessage(
              {
                defaultMessage:
                  'If you and your client intend to continue care past the covered EAP sessions, your client must have active substance abuse, be at risk for hospitalization, or have treatment resistant depression. Your client is <success>ready to use their eligible health plan insurance to pay for sessions.</success>',
                description: 'Message when therapy client with EAP and specialty care is ready to use health plan',
              },
              { success },
            )
          case NOT_ELIGIBLE:
            return formatMessage(
              {
                defaultMessage:
                  'Your clientʼs <error>health insurance does not cover Lyra and care should be discontinued</error> past the covered EAP sessions.',
                description: 'Message when therapy client with EAP and specialty care is not eligible for health plan',
              },
              { error },
            )
          case HEALTH_PLAN_PAYMENT_MISSING:
            return formatMessage(
              {
                defaultMessage:
                  'Your client has entered their health plan information but is missing their payment card for copayments. Please have your client <warning>update their payment card for their health plan section</warning> to complete their profile.',
                description: 'Message when client is missing payment information',
              },
              { warning },
            )
          case NOT_READY:
            return formatMessage(
              {
                defaultMessage:
                  'If you and your client intend to continue care past the covered EAP sessions, your client must have active substance abuse, be at risk for hospitalization, or have treatment resistant depression. Your client <warning>needs to add their health insurance and payment information to their profile.</warning>',
                description:
                  'Message when therapy client with EAP and specialty care needs to add health plan information',
              },
              { warning },
            )
        }
      } else if (
        therapyCoverage?.bhbSupported &&
        !therapyCoverage?.eapSupported &&
        !therapyCoverage?.specialtyBhbSupported
      ) {
        switch (status) {
          case READY:
            return formatMessage(
              {
                defaultMessage:
                  'Your client is <success>ready to use their eligible health plan insurance to pay for sessions.</success>',
                description: 'Message when therapy client is ready to use health plan',
              },
              { success },
            )
          case NOT_ELIGIBLE:
            return formatMessage(
              {
                defaultMessage:
                  'Your clientʼs <error>health insurance does not cover Lyra.</error> Ask your client to contact the Care Navigator Team at care@lyrahealth.com.',
                description: 'Message when therapy client is not eligible for health plan',
              },
              { error },
            )
          case HEALTH_PLAN_PAYMENT_MISSING:
            return formatMessage(
              {
                defaultMessage:
                  'Your client has entered their health plan information but is missing their payment card for copayments. Please have your client <warning>update their payment card for their health plan section</warning> to complete their profile.',
                description: 'Message when client is missing payment information',
              },
              { warning },
            )
          case NOT_READY:
            return formatMessage(
              {
                defaultMessage:
                  'Your client <warning>needs to add their health insurance and payment information</warning> to their profile to start care.',
                description: 'Message when therapy client needs to add health plan information',
              },
              { warning },
            )
        }
      } else if (
        therapyCoverage?.bhbSupported &&
        !therapyCoverage?.eapSupported &&
        therapyCoverage?.specialtyBhbSupported
      ) {
        switch (status) {
          case READY:
            return formatMessage(
              {
                defaultMessage:
                  'Your client is <success>ready to use their eligible health insurance to pay for sessions.</success> If your client has active substance abuse, is at risk for hospitalization, or has treatment resistant depression, you may continue past the recommended 16 sessions.',
                description: 'Message when therapy client with specialty care is ready to use health plan',
              },
              { success },
            )
          case NOT_ELIGIBLE:
            return formatMessage(
              {
                defaultMessage:
                  'Your clientʼs <error>health insurance does not cover Lyra and care should be discontinued.</error>',
                description: 'Message when therapy client with specialty care is not eligible for health plan',
              },
              { error },
            )
          case HEALTH_PLAN_PAYMENT_MISSING:
            return formatMessage(
              {
                defaultMessage:
                  'Your client has entered their health plan information but is missing their payment card for copayments. Please have your client <warning>update their payment card for their health plan section</warning> to complete their profile.',
                description: 'Message when client is missing payment information',
              },
              { warning },
            )
          case NOT_READY:
            return formatMessage(
              {
                defaultMessage:
                  'Your client <warning>needs to add their health insurance and payment information</warning> to their profile to start care. If your client has active substance abuse, is at risk for hospitalization, or has treatment resistant depression, you may continue past the recommended 16 sessions.',
                description: 'Message when therapy client with specialty care needs to add health plan information',
              },
              { warning },
            )
        }
      }
      break
    }
  }

  // default cases
  switch (status) {
    case READY:
      return formatMessage(
        {
          defaultMessage: 'Your client <success>can use Lyra with their health plan</success>{eapSessions}.',
          description: 'Default message when client is ready to use health plan',
        },
        {
          eapSessions: hasEAPSessions ? ' past the covered EAP sessions' : '',
          success,
        },
      )
    case NOT_READY:
      return formatMessage(
        {
          defaultMessage:
            'If you and your client intend to continue{eapSessions}, your client <warning>needs to enter their health plan information to determine coverage.</warning>',
          description: 'Default message when client needs to enter health plan information',
        },
        {
          eapSessions: hasEAPSessions ? ' past the covered EAP sessions' : '',
          warning,
        },
      )
    case HEALTH_PLAN_PAYMENT_MISSING:
      return formatMessage(
        {
          defaultMessage:
            'If you and your client intend to continue{eapSessions}, your client <warning>needs to enter their health plan payment information to determine coverage.</warning>',
          description: 'Default message when client needs to enter payment information',
        },
        {
          eapSessions: hasEAPSessions ? ' past the covered EAP sessions' : '',
          warning,
        },
      )
    case NOT_ELIGIBLE:
    default:
      return formatMessage(
        {
          defaultMessage: 'Your clientʼs <error>health plan does not cover Lyra</error>{eapSessions}.',
          description: 'Default message when client is not eligible for health plan',
        },
        {
          eapSessions: hasEAPSessions ? ' past the covered EAP sessions' : '',
          error,
        },
      )
  }
}
